import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../handler";
import { COOKIE_ACCESS_EXAM } from "../../../utils/storage-constants";
import { LatexRenderer, normalizeLatexString, containsLatexPattern, cleanLatexString } from "../../../components/latex-renderer";
import "./style.sass";

export { LatexRenderer };


const CorrectAnswerDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "A", label: "Option A" },
    { value: "B", label: "Option B" },
    { value: "C", label: "Option C" },
    { value: "D", label: "Option D" },
    { value: "E", label: "Option E" },
  ];

  const currentOption = options.find((opt) => opt.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="custom_dropdown_container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom_dropdown_trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{currentOption.label}</span>
        <svg
          className={`dropdown_chevron ${isOpen ? "rotate" : ""}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4B5563"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom_dropdown_menu">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={`custom_dropdown_item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const formatQuestionTypeLabel = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (trimmed === "All Question Types" || trimmed.toLowerCase() === "all") return "All Question Types";

  const upper = trimmed.toUpperCase().replace(/\s+/g, "_");

  const dictionary = {
    MCQ: "MCQ",
    NUMERIC: "Numerical",
    NUMERICAL: "Numerical",
    SHORT: "Numerical",
    INTEGER: "Integer",
    CONCEPTUAL: "Conceptual",
    ASSERTION_REASON: "Assertion & Reason",
    ASSERTION_REASONING: "Assertion & Reason",
    ASSERTION_AND_REASONING: "Assertion & Reasoning",
    IMAGE_BASED_ASSERTION_AND_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASON: "Image-Based Assertion & Reason",
    IMAGE_BASED_MCQ: "Image-Based MCQ",
    IMAGE_BASED_STATEMENT: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_QUESTION: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_TYPE: "Image-Based Statement",
    MATCH_THE_FOLLOWING: "Match the Following",
    MATCHING: "Match the Following",
    STATEMENT_TYPE: "Statement Type",
    STATEMENT_QUESTION: "Statement Question",
    STATEMENT: "Statement Question",
    PARAGRAPH: "Paragraph",
  };

  if (dictionary[upper]) return dictionary[upper];

  // Clean fallback: convert underscores/hyphens to Title Case
  return trimmed
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLowerCase();
      if (lower === "mcq") return "MCQ";
      if (lower === "and") return "&";
      if (lower === "the") return "the";
      if (lower === "of") return "of";
      return w.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const cleanOptionText = (text) => {
  if (!text) return "";
  const s = String(text).trim();
  if (/^\s*\(?\s*\[?\s*image\s*([_-]?\s*option)?\s*\]?\s*\)?\s*$/i.test(s)) {
    return "";
  }
  return s;
};

export const CustomTypeDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];
  const displayLabel = selectedOption?.label || formatQuestionTypeLabel(value) || value;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="custom_type_dropdown_container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom_type_dropdown_trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        title={displayLabel}
      >
        <span>{displayLabel}</span>
        <svg
          className={`dropdown_arrow ${isOpen ? "rotate" : ""}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4B5563"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom_type_dropdown_menu">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={`custom_type_dropdown_item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};



const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/(%22|%27|["',\\\/])+$/gi, "").trim();
  trimmed = trimmed.replace(/^(%22|%27|["'\\\/])+/gi, "").trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }
  return trimmed;
};

const isValidImageUrl = (url) => {
  const sanitized = sanitizeImageUrl(url);
  if (!sanitized) return false;
  return (
    sanitized.startsWith("http://") ||
    sanitized.startsWith("https://") ||
    sanitized.startsWith("data:image/") ||
    sanitized.startsWith("/")
  );
};

const getValidOptImg = (img) => {
  const sanitized = sanitizeImageUrl(img);
  return isValidImageUrl(sanitized) ? sanitized : null;
};

const handleImageError = (e) => {
  let currentSrc = e.target.src || "";
  if (!e.target.dataset.retryAttempted) {
    e.target.dataset.retryAttempted = "true";
    currentSrc = currentSrc.replace(/(%22|%27|["',\\\/])+$/gi, "");
    if (currentSrc.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
      let newSrc = currentSrc;
      if (newSrc.startsWith("https://")) {
        newSrc = newSrc.replace("https://", "http://");
      }
      if (!newSrc.includes(":8001")) {
        newSrc = newSrc.replace(
          "visal1chatbotvm.southindia.cloudapp.azure.com",
          "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
        );
      }
      if (newSrc !== currentSrc) {
        e.target.src = newSrc;
        return;
      }
    }
  }
  e.target.style.display = "none";
};

const parseIsUsed = (isUsed, usedCount) => {
  if (isUsed !== undefined && isUsed !== null) {
    if (typeof isUsed === "boolean") return isUsed;
    if (typeof isUsed === "string") {
      const s = isUsed.trim().toLowerCase();
      if (s === "false" || s === "0" || s === "no") return false;
      if (s === "true" || s === "1" || s === "yes") return true;
    }
    if (typeof isUsed === "number") return isUsed > 0;
    return Boolean(isUsed);
  }
  if (usedCount !== undefined && usedCount !== null) {
    return Number(usedCount) > 0;
  }
  return undefined;
};

export const renderHighlightedString = (text, highlight) => {
  if (!highlight || !highlight.trim() || !text) return text;
  try {
    const escaped = highlight.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    const parts = String(text).split(regex);
    if (parts.length <= 1) return text;
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="qb_search_highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  } catch {
    return text;
  }
};

export const ImportedQuestionsModal = ({
  testName = "",
  exam: initialExam,
  subject: initialSubject,
  level: initialLevel = "",
  isImportFlow = false,
  chooseParams = null,
  onClose,
  onComplete,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All Question Types");
  const [filterLevel, setFilterLevel] = useState("All Levels");
  const [level, setLevel] = useState(initialLevel || chooseParams?.level || chooseParams?.difficulty || "");
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API State
  const savedExam = typeof window !== "undefined" ? localStorage.getItem(COOKIE_ACCESS_EXAM) : null;
  const defaultExam = (savedExam && savedExam.toUpperCase().includes("JEE")) ? "JEE" : "NEET";

  const [exam, setExam] = useState(initialExam || defaultExam);
  const [subject, setSubject] = useState(initialSubject || (defaultExam === "JEE" ? "Physics" : "Biology"));

  useEffect(() => {
    if (initialExam) setExam(initialExam);
  }, [initialExam]);

  useEffect(() => {
    if (initialSubject) setSubject(initialSubject);
  }, [initialSubject]);

  useEffect(() => {
    if (initialLevel) setLevel(initialLevel);
  }, [initialLevel]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [testName, exam, subject, isImportFlow]);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState("PARSED");
  const [questionLimit, setQuestionLimit] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [editPromptText, setEditPromptText] = useState("");
  const [editOptionA, setEditOptionA] = useState("");
  const [editOptionB, setEditOptionB] = useState("");
  const [editOptionC, setEditOptionC] = useState("");
  const [editOptionD, setEditOptionD] = useState("");
  const [editOptionE, setEditOptionE] = useState("");
  const [editCorrectAnswer, setEditCorrectAnswer] = useState("A");
  const [editSolution, setEditSolution] = useState("");
  const [editStemImage, setEditStemImage] = useState(null);
  const [editOptionImages, setEditOptionImages] = useState({ A: null, B: null, C: null, D: null, E: null });
  const [uploadingTarget, setUploadingTarget] = useState(null);

  // Image Zoom Lightbox Modal State
  const [zoomModalImage, setZoomModalImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1.5);

  const handleOpenZoom = (e, fallbackSrc, title) => {
    if (e && e.stopPropagation) e.stopPropagation();
    let finalSrc = fallbackSrc;
    const parentBox = e?.currentTarget?.closest(".img_preview_box");
    const activeImg = parentBox?.querySelector("img");
    if (activeImg && (activeImg.currentSrc || activeImg.src)) {
      finalSrc = activeImg.currentSrc || activeImg.src;
    }
    setZoomScale(1.5);
    setZoomModalImage({ src: finalSrc, title });
  };

  const api = useApi();
  const {
    fetchQuestionBank,
    fetchImportedUniqueQuestions,
    fetchImportedQuestions,
    addQuestionsToPaper,
    addCategorizedQuestionsToPaper,
    editQuestion,
    uploadQuestionImage,
    deleteQuestionImage,
    completeQuestionPaper,
    searchCategorizedQuestions,
    fetchMcqQuestionTypes,
    fetchMcqConcepts,
  } = useQuestionBankHandler(api);

  const [questions, setQuestions] = useState([]);
  const [filterConcept, setFilterConcept] = useState("All Concepts");
  const [filterValidation, setFilterValidation] = useState("all");
  const [onlyNotUsed, setOnlyNotUsed] = useState(false);
  const [dynamicTypeOptions, setDynamicTypeOptions] = useState([]);
  const [dynamicConceptOptions, setDynamicConceptOptions] = useState([]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await fetchMcqQuestionTypes();
        const rawTypes = Array.isArray(res)
          ? res
          : Array.isArray(res?.question_types)
            ? res.question_types
            : Array.isArray(res?.question_type)
              ? res.question_type
              : Array.isArray(res?.types)
                ? res.types
                : Array.isArray(res?.data)
                  ? res.data
                  : [];

        if (rawTypes && rawTypes.length > 0) {
          const formattedOptions = [
            { value: "All Question Types", label: "All Question Types" },
            ...rawTypes.map((typeItem) => {
              const rawVal = typeof typeItem === "object" && typeItem !== null
                ? (typeItem.value || typeItem.question_type || typeItem.name || typeItem.label || "")
                : String(typeItem).trim();
              const formattedLabel = formatQuestionTypeLabel(rawVal);
              return { value: rawVal, label: formattedLabel };
            }).filter((opt) => Boolean(opt.value)),
          ];
          setDynamicTypeOptions(formattedOptions);
        }
      } catch (err) {
        console.error("Error loading question types:", err);
      }
    };

    loadTypes();
  }, []);

  useEffect(() => {
    if (!isImportFlow || chooseParams) {
      const loadConcepts = async () => {
        try {
          const testSubTopic =
            chooseParams?.subTopic ??
            chooseParams?.sub_topic ??
            chooseParams?.selectedTest?.sub_topic ??
            chooseParams?.selectedTest?.subTopic;

          // 1. If test has sub_topic / sub topics defined from /teacher/test/list:
          if (testSubTopic && typeof testSubTopic === "string" && testSubTopic.trim() !== "") {
            const subTopicsList = testSubTopic
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);

            if (subTopicsList.length > 0) {
              const formattedOptions = [
                { value: "All Concepts", label: "All Concepts" },
                ...subTopicsList.map((item) => ({ value: item, label: item })),
              ];
              setDynamicConceptOptions(formattedOptions);
              return;
            }
          } else if (Array.isArray(testSubTopic) && testSubTopic.length > 0) {
            const formattedOptions = [
              { value: "All Concepts", label: "All Concepts" },
              ...testSubTopic.map((item) => {
                const str = typeof item === "object" && item !== null
                  ? (item.label || item.name || item.value || item.concept || item.sub_topic || "")
                  : String(item).trim();
                return { value: str, label: str };
              }).filter((opt) => Boolean(opt.value)),
            ];
            setDynamicConceptOptions(formattedOptions);
            return;
          }

          // 2. If concept not selected (sub_topic is null, empty or missing), fetch from API:
          const res = await fetchMcqConcepts({
            exam: chooseParams?.exam || exam || "NEET",
            className: chooseParams?.className || chooseParams?.class || "",
            subject: chooseParams?.subject || subject || "",
            chapter: chooseParams?.chapter || "",
          });
          const rawConcepts = Array.isArray(res)
            ? res
            : Array.isArray(res?.concepts)
              ? res.concepts
              : Array.isArray(res?.concept)
                ? res.concept
                : Array.isArray(res?.sub_topics)
                  ? res.sub_topics
                  : Array.isArray(res?.data?.sub_topics)
                    ? res.data.sub_topics
                    : Array.isArray(res?.data)
                      ? res.data
                      : [];

          if (rawConcepts && rawConcepts.length > 0) {
            const formattedOptions = [
              { value: "All Concepts", label: "All Concepts" },
              ...rawConcepts.map((item) => {
                const str = typeof item === "object" && item !== null
                  ? (item.label || item.name || item.value || item.concept || item.sub_topic || "")
                  : String(item).trim();
                return { value: str, label: str };
              }).filter((opt) => Boolean(opt.value)),
            ];
            setDynamicConceptOptions(formattedOptions);
          } else {
            setDynamicConceptOptions([{ value: "All Concepts", label: "All Concepts" }]);
          }
        } catch (err) {
          console.error("Error loading concepts:", err);
          setDynamicConceptOptions([{ value: "All Concepts", label: "All Concepts" }]);
        }
      };

      loadConcepts();
    }
  }, [isImportFlow, chooseParams, exam, subject]);

  const handleStemImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingQuestion) return;
    setUploadingTarget("stem");
    const questionSource =
      chooseParams?.optionType === "ai_generated"
        ? "ai"
        : chooseParams?.optionType === "question_bank"
          ? "bank"
          : (editingQuestion?.source || (isImportFlow ? "import" : "bank"));
    const res = await uploadQuestionImage(editingQuestion.id, "stem", file, questionSource);
    setUploadingTarget(null);
    if (res && (res.status === "success" || res.url || res.image_urls)) {
      const newUrl = res.url || (res.image_urls && res.image_urls[0]) || null;
      const updatedStemUrls = res.image_urls || (newUrl ? [newUrl] : []);
      setEditStemImage(newUrl);
      setEditingQuestion((prev) => (prev ? { ...prev, imageUrls: updatedStemUrls } : null));
      setQuestions((prev) =>
        prev.map((q) => (q.id === editingQuestion.id ? { ...q, imageUrls: updatedStemUrls } : q))
      );
    }
  };

  const handleStemImageDelete = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!editingQuestion) return;
    setUploadingTarget("stem");
    const currentUrl = editStemImage || "";
    const questionSource =
      chooseParams?.optionType === "ai_generated"
        ? "ai"
        : chooseParams?.optionType === "question_bank"
          ? "bank"
          : (editingQuestion?.source || (isImportFlow ? "import" : "bank"));
    const res = await deleteQuestionImage(editingQuestion.id, "stem", currentUrl, questionSource);
    setUploadingTarget(null);
    if (res) {
      const updatedStemUrls = res.image_urls || [];
      setEditStemImage(null);
      setEditingQuestion((prev) => (prev ? { ...prev, imageUrls: updatedStemUrls, image_url: null, image: null } : null));
      setQuestions((prev) =>
        prev.map((q) => (q.id === editingQuestion.id ? { ...q, imageUrls: updatedStemUrls, image_url: null, image: null } : q))
      );
    }
  };

  const handleOptionImageUpload = async (optionKey, e) => {
    const file = e.target.files?.[0];
    if (!file || !editingQuestion) return;
    const targetLabel = optionKey.toLowerCase();
    setUploadingTarget(optionKey);
    const questionSource =
      chooseParams?.optionType === "ai_generated"
        ? "ai"
        : chooseParams?.optionType === "question_bank"
          ? "bank"
          : (editingQuestion?.source || (isImportFlow ? "import" : "bank"));
    const res = await uploadQuestionImage(editingQuestion.id, targetLabel, file, questionSource);
    setUploadingTarget(null);
    if (res && (res.status === "success" || res.url || res.option_images)) {
      const newUrl = res.url || res.option_images?.[targetLabel] || res.option_images?.[optionKey] || null;
      setEditOptionImages((prev) => ({ ...prev, [optionKey]: newUrl }));
      setEditingQuestion((prev) => {
        if (!prev) return null;
        const updatedOptImgs = { ...(prev.optionImages || {}), [optionKey]: newUrl };
        return { ...prev, optionImages: updatedOptImgs };
      });
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== editingQuestion.id) return q;
          const updatedOptImgs = { ...(q.optionImages || {}), [optionKey]: newUrl };
          return { ...q, optionImages: updatedOptImgs };
        })
      );
    }
  };

  const handleOptionImageDelete = async (optionKey, e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (!editingQuestion) return;
    const targetLabel = optionKey.toLowerCase();
    setUploadingTarget(optionKey);
    const currentUrl = editOptionImages?.[optionKey] || "";
    const questionSource =
      chooseParams?.optionType === "ai_generated"
        ? "ai"
        : chooseParams?.optionType === "question_bank"
          ? "bank"
          : (editingQuestion?.source || (isImportFlow ? "import" : "bank"));
    const res = await deleteQuestionImage(editingQuestion.id, targetLabel, currentUrl, questionSource);
    setUploadingTarget(null);
    if (res) {
      setEditOptionImages((prev) => ({ ...prev, [optionKey]: null }));
      setEditingQuestion((prev) => {
        if (!prev) return null;
        const updatedOptImgs = { ...(prev.optionImages || {}), [optionKey]: null };
        return { ...prev, optionImages: updatedOptImgs };
      });
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== editingQuestion.id) return q;
          const updatedOptImgs = { ...(q.optionImages || {}), [optionKey]: null };
          return { ...q, optionImages: updatedOptImgs };
        })
      );
    }
  };

  const getNormalizedCorrectAnswer = (ans) => {
    if (!ans) return "A";
    const str = String(ans).trim().toUpperCase();
    if (str === "A" || str === "OPTION A" || str === "OPTIONA") return "A";
    if (str === "B" || str === "OPTION B" || str === "OPTIONB") return "B";
    if (str === "C" || str === "OPTION C" || str === "OPTIONC") return "C";
    if (str === "D" || str === "OPTION D" || str === "OPTIOND") return "D";
    if (str === "E" || str === "OPTION E" || str === "OPTIONE") return "E";
    if (str.startsWith("OPTION ")) return str.replace("OPTION ", "").trim();
    return "A";
  };

  const formatCanonicalOptions = (optTexts, optImgs, isNumeric, originalQ) => {
    if (isNumeric) {
      return { options: {}, optionImages: {}, hasOptE: false };
    }

    const cleanedA = optTexts?.a || "";
    const cleanedB = optTexts?.b || "";
    const cleanedC = optTexts?.c || "";
    const cleanedD = optTexts?.d || "";
    const cleanedE = optTexts?.e || "";

    const imgA = optImgs?.a || optImgs?.A || null;
    const imgB = optImgs?.b || optImgs?.B || null;
    const imgC = optImgs?.c || optImgs?.C || null;
    const imgD = optImgs?.d || optImgs?.D || null;
    const imgE = optImgs?.e || optImgs?.E || null;

    // Check if Option E is a real choice (has non-empty text or image)
    const hasOptE = Boolean(cleanedE.trim() || imgE);

    // Option D is active if it has text/image, or option E exists, or original question had option D
    const origHasD = Boolean(originalQ?.optionD?.trim() || originalQ?.optionImages?.D || originalQ?.options?.d || originalQ?.options?.D);
    const hasOptD = Boolean(cleanedD.trim() || imgD || hasOptE || origHasD);

    // Option C is active if it has text/image, or option D exists, or original question had option C
    const origHasC = Boolean(originalQ?.optionC?.trim() || originalQ?.optionImages?.C || originalQ?.options?.c || originalQ?.options?.C);
    const hasOptC = Boolean(cleanedC.trim() || imgC || hasOptD || origHasC);

    const activeKeys = ["a", "b"];
    if (hasOptC) activeKeys.push("c");
    if (hasOptD) activeKeys.push("d");
    if (hasOptE) activeKeys.push("e");

    const textMap = { a: cleanedA, b: cleanedB, c: cleanedC, d: cleanedD, e: cleanedE };
    const imgMap = { a: imgA, b: imgB, c: imgC, d: imgD, e: imgE };

    const canonicalOptions = {};
    const canonicalOptionImages = {};

    activeKeys.forEach((key) => {
      canonicalOptions[key] = textMap[key] || "";
      canonicalOptionImages[key] = imgMap[key] || null;
    });

    return {
      options: canonicalOptions,
      optionImages: canonicalOptionImages,
      hasOptE,
    };
  };

  const formatCanonicalCorrectAnswer = (ans, isNumeric) => {
    if (isNumeric) return String(ans || "").trim();
    const raw = String(ans || "").trim();
    const upper = raw.toUpperCase();
    if (upper === "A" || upper === "OPTION A" || upper === "OPTION_A" || upper === "OPTIONA") return "a";
    if (upper === "B" || upper === "OPTION B" || upper === "OPTION_B" || upper === "OPTIONB") return "b";
    if (upper === "C" || upper === "OPTION C" || upper === "OPTION_C" || upper === "OPTIONC") return "c";
    if (upper === "D" || upper === "OPTION D" || upper === "OPTION_D" || upper === "OPTIOND") return "d";
    if (upper === "E" || upper === "OPTION E" || upper === "OPTION_E" || upper === "OPTIONE") return "e";
    return raw.toLowerCase();
  };

  const handleCorrectAnswerChange = async (questionId, newAnswer) => {
    const targetQ = questions.find((q) => q.id === questionId);
    if (!targetQ) return;

    const isNumeric =
      (targetQ.type || "").toUpperCase() === "NUMERIC" ||
      (targetQ.type || "").toUpperCase() === "SHORT" ||
      (targetQ.type || "").toUpperCase() === "NUMERICAL";

    const optTexts = {
      a: normalizeLatexString(targetQ.optionA || ""),
      b: normalizeLatexString(targetQ.optionB || ""),
      c: normalizeLatexString(targetQ.optionC || ""),
      d: normalizeLatexString(targetQ.optionD || ""),
      e: normalizeLatexString(targetQ.optionE || ""),
    };
    const optImgs = targetQ.optionImages || {};

    const { options: canonicalOptions, optionImages: canonicalOptionImages } = formatCanonicalOptions(
      optTexts,
      optImgs,
      isNumeric,
      targetQ
    );

    const canonicalAnswer = formatCanonicalCorrectAnswer(newAnswer, isNumeric);

    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, correct_answer: canonicalAnswer } : q))
    );

    const payload = {
      question_text: normalizeLatexString(targetQ.text),
      options: canonicalOptions,
      option_images: canonicalOptionImages,
      image_urls: targetQ.imageUrls || [],
      correct_answer: canonicalAnswer,
      solution: normalizeLatexString(targetQ.solution),
    };

    const isAi = chooseParams?.optionType === "ai_generated" || targetQ?.source === "ai";
    await editQuestion(questionId, payload, isAi);
  };

  const handleEditSave = async () => {
    if (!editingQuestion) return;
    const questionId = editingQuestion.id;

    const cleanedPrompt = normalizeLatexString(editPromptText);
    const cleanedOptA = normalizeLatexString(editOptionA);
    const cleanedOptB = normalizeLatexString(editOptionB);
    const cleanedOptC = normalizeLatexString(editOptionC);
    const cleanedOptD = normalizeLatexString(editOptionD);
    const cleanedOptE = normalizeLatexString(editOptionE);
    const cleanedSol = normalizeLatexString(editSolution);

    const currentOptImgs = editOptionImages || editingQuestion.optionImages || {};

    const isNumeric =
      (editingQuestion.type || "").toUpperCase() === "NUMERIC" ||
      (editingQuestion.type || "").toUpperCase() === "SHORT" ||
      (editingQuestion.type || "").toUpperCase() === "NUMERICAL";

    const optTexts = {
      a: cleanedOptA,
      b: cleanedOptB,
      c: cleanedOptC,
      d: cleanedOptD,
      e: cleanedOptE,
    };

    const { options: canonicalOptions, optionImages: canonicalOptionImages, hasOptE } = formatCanonicalOptions(
      optTexts,
      currentOptImgs,
      isNumeric,
      editingQuestion
    );

    const currentStemUrls = editStemImage ? [editStemImage] : (editingQuestion.imageUrls || []);
    const canonicalAnswer = formatCanonicalCorrectAnswer(editCorrectAnswer, isNumeric);

    const payload = {
      question_text: cleanedPrompt,
      options: canonicalOptions,
      option_images: canonicalOptionImages,
      image_urls: currentStemUrls,
      correct_answer: canonicalAnswer,
      solution: cleanedSol,
    };

    const isAi = chooseParams?.optionType === "ai_generated" || editingQuestion?.source === "ai";
    const res = await editQuestion(questionId, payload, isAi);
    const resOptImgs = res?.question?.option_images || res?.option_images || canonicalOptionImages;
    const updatedOptImgs = {
      A: resOptImgs.A || resOptImgs.a || currentOptImgs.A || currentOptImgs.a || null,
      B: resOptImgs.B || resOptImgs.b || currentOptImgs.B || currentOptImgs.b || null,
      C: resOptImgs.C || resOptImgs.c || currentOptImgs.C || currentOptImgs.c || null,
      D: resOptImgs.D || resOptImgs.d || currentOptImgs.D || currentOptImgs.d || null,
      E: hasOptE ? (resOptImgs.E || resOptImgs.e || currentOptImgs.E || currentOptImgs.e || null) : null,
    };

    const hasAnyOptText = Boolean(cleanedOptA || cleanedOptB || cleanedOptC || cleanedOptD || (hasOptE && cleanedOptE));
    const hasAnyOptImg = Boolean(updatedOptImgs.A || updatedOptImgs.B || updatedOptImgs.C || updatedOptImgs.D || (hasOptE && updatedOptImgs.E));
    const isValidStructure = isNumeric ? Boolean(cleanedPrompt && cleanedPrompt.trim()) : (hasAnyOptText || hasAnyOptImg);

    const apiIsValid = (res && res.is_valid !== undefined) ? Boolean(res.is_valid) : (res?.question?.is_valid !== undefined ? Boolean(res.question.is_valid) : true);
    const newIsValid = apiIsValid && isValidStructure;

    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === questionId
          ? {
            ...q,
            text: cleanedPrompt,
            optionA: cleanedOptA,
            optionB: cleanedOptB,
            optionC: cleanedOptC,
            optionD: cleanedOptD,
            optionE: hasOptE ? cleanedOptE : "",
            optionImages: updatedOptImgs,
            imageUrls: currentStemUrls,
            correct_answer: canonicalAnswer,
            solution: cleanedSol,
            isValid: newIsValid,
            validationError: newIsValid ? null : (res?.detail || res?.message || q.validationError),
          }
          : q
      )
    );

    setEditingQuestion(null);
    setEditPromptText("");
    setEditOptionA("");
    setEditOptionB("");
    setEditOptionC("");
    setEditOptionD("");
    setEditOptionE("");
    setEditSolution("");
  };

  useEffect(() => {
    let isMounted = true;
    let pollTimer = null;

    const loadQuestions = async () => {
      setLoading(true);
      try {
        const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
        const validTestName = (cleanName && !cleanName.includes("@")) ? cleanName : "";
        if (isImportFlow && !validTestName) {
          setLoading(false);
          return;
        }

        let res = null;
        if (isImportFlow) {
          const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "";
          res = await fetchImportedQuestions({
            testName: validTestName,
            page,
            questionType: typeParam,
            search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
          });
        } else if (chooseParams?.optionType === "question_bank") {
          const userTargetCount = chooseParams.noOfQuestions ? parseInt(chooseParams.noOfQuestions, 10) : 10;
          if (userTargetCount) setQuestionLimit(userTargetCount);

          const pageSize = 10;
          const testSubTopic =
            chooseParams?.subTopic ??
            chooseParams?.sub_topic ??
            chooseParams?.selectedTest?.sub_topic ??
            chooseParams?.selectedTest?.subTopic;

          const conceptParam = (filterConcept && filterConcept !== "All Concepts")
            ? filterConcept
            : (testSubTopic && String(testSubTopic).trim() ? String(testSubTopic).trim() : "all");
          const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "all";

          res = await fetchImportedUniqueQuestions({
            testName: validTestName,
            exam: chooseParams.exam || exam || "NEET",
            subject: chooseParams.subject || subject || "Biology",
            className: chooseParams.className || chooseParams.class || "",
            chapter: chooseParams.chapter || "",
            concept: conceptParam,
            questionType: typeParam,
            search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
            level: (filterLevel && filterLevel !== "All Levels") ? filterLevel : "",
            page,
            limit: pageSize,
            pageSize,
          });
        } else if (chooseParams?.optionType === "ai_generated" || chooseParams) {
          const userTargetCount = chooseParams.noOfQuestions ? parseInt(chooseParams.noOfQuestions, 10) : 10;
          if (userTargetCount) setQuestionLimit(userTargetCount);

          const pageSize = 10;
          const testSubTopic =
            chooseParams?.subTopic ??
            chooseParams?.sub_topic ??
            chooseParams?.selectedTest?.sub_topic ??
            chooseParams?.selectedTest?.subTopic;

          const conceptParam = (filterConcept && filterConcept !== "All Concepts")
            ? filterConcept
            : (testSubTopic && String(testSubTopic).trim() ? String(testSubTopic).trim() : "all");
          const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "all";
          res = await searchCategorizedQuestions({
            exam: chooseParams.exam || exam || "NEET",
            className: chooseParams.className || chooseParams.class || "",
            subject: chooseParams.subject || subject || "",
            chapter: chooseParams.chapter || "",
            concept: conceptParam,
            questionType: typeParam,
            search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
            limit: pageSize,
            offset: (page - 1) * pageSize,
          });
        } else {
          res = await fetchQuestionBank({ exam, subject, page });
          if (!res || !res.questions || res.questions.length === 0) {
            res = await fetchQuestionBank({ page });
          }
        }

        if (isMounted) {
          const currentStatus = res?.job_status || "PARSED";
          setJobStatus(currentStatus);

          if (res && res.question_limit !== undefined && res.question_limit !== null) {
            setQuestionLimit(res.question_limit);
          }

          const rawQuestions = Array.isArray(res)
            ? res
            : Array.isArray(res?.questions)
              ? res.questions
              : Array.isArray(res?.data)
                ? res.data
                : [];

          if (rawQuestions && rawQuestions.length > 0) {
            const getValidOptImg = (img) => (isValidImageUrl(img) ? img.trim() : null);
            const formatted = rawQuestions.map((q, idx) => {
              const optA = cleanOptionText(q.options?.a || q.options?.A || q.optionA || q.option_a || "");
              const optB = cleanOptionText(q.options?.b || q.options?.B || q.optionB || q.option_b || "");
              const optC = cleanOptionText(q.options?.c || q.options?.C || q.optionC || q.option_c || "");
              const optD = cleanOptionText(q.options?.d || q.options?.D || q.optionD || q.option_d || "");
              const optE = cleanOptionText(q.options?.e || q.options?.E || q.optionE || q.option_e || "");

              const hasOptText =
                Boolean(optA && optA.trim()) ||
                Boolean(optB && optB.trim());

              const hasOptImg =
                Boolean(getValidOptImg(q.option_images?.a || q.option_images?.A)) ||
                Boolean(getValidOptImg(q.option_images?.b || q.option_images?.B)) ||
                Boolean(getValidOptImg(q.option_images?.c || q.option_images?.C)) ||
                Boolean(getValidOptImg(q.option_images?.d || q.option_images?.D)) ||
                Boolean(getValidOptImg(q.option_images?.e || q.option_images?.E));

              const rawCategory = String(q.category || q.question_type || q.type || "").trim();

              const categoryTag = formatQuestionTypeLabel(rawCategory);
              const isNumericType = ["SHORT", "NUMERICAL", "NUMERIC", "INTEGER"].includes(rawCategory.toUpperCase());

              const hasOptions = hasOptText || hasOptImg;
              const isMcq = hasOptions && !isNumericType;
              const finalType = categoryTag || (isMcq ? "MCQ" : "Numeric");
              const qId = q.question_id || q.id || q._id || `UQ_P${page}_${idx + 1}`;

              const rawImageUrls = Array.isArray(q.image_urls)
                ? q.image_urls
                : q.image_url
                  ? [q.image_url]
                  : q.image
                    ? [q.image]
                    : [];

              const imageUrls = rawImageUrls
                .map(sanitizeImageUrl)
                .filter(Boolean)
                .filter(isValidImageUrl);

              const optionImages = q.option_images
                ? {
                  A: getValidOptImg(q.option_images.a || q.option_images.A),
                  B: getValidOptImg(q.option_images.b || q.option_images.B),
                  C: getValidOptImg(q.option_images.c || q.option_images.C),
                  D: getValidOptImg(q.option_images.d || q.option_images.D),
                  E: getValidOptImg(q.option_images.e || q.option_images.E),
                }
                : null;

              const rawAnswer = (q.correct_answer !== undefined && q.correct_answer !== null)
                ? String(q.correct_answer).trim()
                : (q.answer !== undefined && q.answer !== null ? String(q.answer).trim() : "");

              // Match rawAnswer to letter A/B/C/D/E if rawAnswer contains full option text or letter
              const resolveAnswerLetter = (ans) => {
                if (!ans) return "";
                const upper = ans.toUpperCase();
                if (upper === "A" || upper === "OPTION A" || upper === "OPTIONA") return "A";
                if (upper === "B" || upper === "OPTION B" || upper === "OPTIONB") return "B";
                if (upper === "C" || upper === "OPTION C" || upper === "OPTIONC") return "C";
                if (upper === "D" || upper === "OPTION D" || upper === "OPTIOND") return "D";
                if (upper === "E" || upper === "OPTION E" || upper === "OPTIONE") return "E";

                const cleanAns = ans.toLowerCase().replace(/\s+/g, " ");
                if (optA && cleanAns === optA.trim().toLowerCase().replace(/\s+/g, " ")) return "A";
                if (optB && cleanAns === optB.trim().toLowerCase().replace(/\s+/g, " ")) return "B";
                if (optC && cleanAns === optC.trim().toLowerCase().replace(/\s+/g, " ")) return "C";
                if (optD && cleanAns === optD.trim().toLowerCase().replace(/\s+/g, " ")) return "D";
                if (optE && cleanAns === optE.trim().toLowerCase().replace(/\s+/g, " ")) return "E";
                return "";
              };

              // For MCQ: resolve to A/B/C/D/E letter. For Numeric: keep raw answer as-is.
              const rawCorrectAns = isMcq ? resolveAnswerLetter(rawAnswer) : rawAnswer;

              const rawError = q.validation_errors || q.validationError || q.error_message || q.error || q.reasons;
              const hasRawError = Boolean(
                rawError &&
                (typeof rawError === "string"
                  ? rawError.trim()
                  : Array.isArray(rawError)
                    ? rawError.length > 0
                    : true)
              );

              const apiIsValid = q.is_valid !== undefined && q.is_valid !== null ? Boolean(q.is_valid) : true;

              const isValid = apiIsValid && !hasRawError;
              const validationError = !isValid
                ? typeof rawError === "string" && rawError.trim()
                  ? rawError.trim()
                  : Array.isArray(rawError) && rawError.length > 0
                    ? rawError.join(". ")
                    : "This question has errors and needs manual verification before selecting"
                : null;

              const qText = q.question || q.question_text || q.text || q.stem || "";
              const rawLevel =
                q.level ||
                q.difficulty ||
                q.difficulty_level ||
                q.question_level ||
                initialLevel ||
                level ||
                chooseParams?.level ||
                chooseParams?.difficulty ||
                "";

              return {
                id: qId,
                source: chooseParams?.optionType === "ai_generated"
                  ? "ai"
                  : chooseParams?.optionType === "question_bank"
                    ? "bank"
                    : (isImportFlow ? "import" : (q.source || "bank")),
                type: finalType,
                rawType: rawCategory,
                text: normalizeLatexString(qText),
                correct_answer: isMcq
                  ? (rawCorrectAns || "A")
                  : (rawCorrectAns || ""),
                solution: normalizeLatexString(q.solution || q.explanation || ""),
                optionA: normalizeLatexString(optA),
                optionB: normalizeLatexString(optB),
                optionC: normalizeLatexString(optC),
                optionD: normalizeLatexString(optD),
                optionE: normalizeLatexString(optE),
                imageUrls,
                optionImages,
                isValid,
                validationError,
                is_used: parseIsUsed(q.is_used, q.used_count),
                used_count: q.used_count !== undefined && q.used_count !== null ? Number(q.used_count) : undefined,
                level: rawLevel,
              };
            });
            setQuestions(formatted);
            if (!level && (res.level || res.difficulty)) {
              setLevel(res.level || res.difficulty);
            } else if (!level) {
              const detectedLvl = formatted.find((item) => item.level)?.level;
              if (detectedLvl) setLevel(detectedLvl);
            }
            const total = res.total_count || res.total || formatted.length;
            setTotalCount(total);
            const detectedPageSize = res.limit || res.page_size || 10;
            const pagesCount = res.total_pages || (total > 0 ? Math.ceil(total / detectedPageSize) : 1);
            setTotalPages(pagesCount);
            setLoading(false);

            if (isImportFlow && ["PARSING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(String(currentStatus).toUpperCase())) {
              pollTimer = setTimeout(() => {
                if (isMounted) {
                  setPollCount((prev) => prev + 1);
                }
              }, 2500);
            }
          } else if (isImportFlow && ["PARSING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(String(currentStatus).toUpperCase())) {
            setQuestions([]);
            setTotalCount(0);
            setTotalPages(1);
            setLoading(false);
            pollTimer = setTimeout(() => {
              if (isMounted) {
                setPollCount((prev) => prev + 1);
              }
            }, 2500);
          } else {
            if (page > 1) {
              setTotalPages((prevPages) => Math.max(1, Math.min(prevPages, page - 1)));
              setPage((prevPage) => Math.max(1, prevPage - 1));
            } else {
              setQuestions([]);
              setTotalCount(0);
              setTotalPages(1);
            }
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("Failed to load questions from API", err);
        if (isMounted) {
          setQuestions([]);
          setTotalCount(0);
          setLoading(false);
        }
      }
    };

    loadQuestions();

    return () => {
      isMounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [testName, exam, subject, page, isImportFlow, pollCount, filterType, filterConcept, filterLevel, debouncedSearchTerm, chooseParams]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAllChange = async (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const currentPageValidIds = questions.filter((q) => q.isValid).map((q) => q.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageValidIds.forEach((id) => next.add(id));
        return next;
      });

      if (totalPages > 1) {
        try {
          const cleanName = (testName || "").replace(/[–—]/g, "-").trim();
          const validTestName = (cleanName && !cleanName.includes("@")) ? cleanName : "";
          if (isImportFlow && !validTestName) return;

          const pagePromises = [];
          for (let p = 1; p <= totalPages; p++) {
            if (p === page) continue;
            if (isImportFlow) {
              const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "";
              pagePromises.push(
                fetchImportedQuestions({
                  testName: validTestName,
                  page: p,
                  questionType: typeParam,
                  search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
                }).then((res) => ({ res, p }))
              );
            } else if (chooseParams?.optionType === "question_bank") {
              const pageSize = 10;
              const testSubTopic =
                chooseParams?.subTopic ??
                chooseParams?.sub_topic ??
                chooseParams?.selectedTest?.sub_topic ??
                chooseParams?.selectedTest?.subTopic;

              const conceptParam = (filterConcept && filterConcept !== "All Concepts")
                ? filterConcept
                : (testSubTopic && String(testSubTopic).trim() ? String(testSubTopic).trim() : "all");
              const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "all";

              pagePromises.push(
                fetchImportedUniqueQuestions({
                  testName: validTestName,
                  exam: chooseParams.exam || exam,
                  subject: chooseParams.subject || subject,
                  className: chooseParams.className || chooseParams.class || "",
                  chapter: chooseParams.chapter || "",
                  concept: conceptParam,
                  questionType: typeParam,
                  search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
                  level: (filterLevel && filterLevel !== "All Levels") ? filterLevel : "",
                  page: p,
                  limit: pageSize,
                  pageSize,
                }).then((res) => ({ res, p }))
              );
            } else if (chooseParams?.optionType === "ai_generated" || chooseParams) {
              const pageSize = 10;
              const testSubTopic =
                chooseParams?.subTopic ??
                chooseParams?.sub_topic ??
                chooseParams?.selectedTest?.sub_topic ??
                chooseParams?.selectedTest?.subTopic;

              const conceptParam = (filterConcept && filterConcept !== "All Concepts")
                ? filterConcept
                : (testSubTopic && String(testSubTopic).trim() ? String(testSubTopic).trim() : "all");
              const typeParam = (filterType && filterType !== "All Question Types") ? filterType : "all";
              pagePromises.push(
                searchCategorizedQuestions({
                  exam: chooseParams?.exam || exam || "NEET",
                  className: chooseParams?.className || chooseParams?.class || "",
                  subject: chooseParams?.subject || subject || "",
                  chapter: chooseParams?.chapter || "",
                  concept: conceptParam,
                  questionType: typeParam,
                  search: debouncedSearchTerm ? debouncedSearchTerm.trim() : "",
                  limit: pageSize,
                  offset: (p - 1) * pageSize,
                }).then((res) => ({ res, p }))
              );
            } else {
              pagePromises.push(
                fetchQuestionBank({ exam, subject, page: p }).then(async (res) => {
                  if (!res || !res.questions || res.questions.length === 0) {
                    const fallbackRes = await fetchQuestionBank({ page: p });
                    return { res: fallbackRes, p };
                  }
                  return { res, p };
                })
              );
            }
          }

          const results = await Promise.all(pagePromises);
          const allFetchedIds = [...currentPageValidIds];

          results.forEach(({ res, p }) => {
            const resQuestions = Array.isArray(res)
              ? res
              : Array.isArray(res?.questions)
                ? res.questions
                : Array.isArray(res?.data)
                  ? res.data
                  : [];

            if (resQuestions && resQuestions.length > 0) {
              resQuestions.forEach((q, idx) => {
                const apiIsValid = q.is_valid !== undefined && q.is_valid !== null ? Boolean(q.is_valid) : true;
                const rawError = q.validation_errors || q.validationError || q.error_message || q.error || q.reasons;
                const hasRawError = Boolean(
                  rawError &&
                  (typeof rawError === "string"
                    ? rawError.trim()
                    : Array.isArray(rawError)
                      ? rawError.length > 0
                      : true)
                );
                if (apiIsValid && !hasRawError) {
                  const qId = q.question_id || q.id || q._id || `UQ_P${p}_${idx + 1}`;
                  allFetchedIds.push(qId);
                }
              });
            }
          });

          setSelectedIds(new Set(allFetchedIds));
        } catch (err) {
          console.error("Failed to fetch all questions for Select All:", err);
        }
      }
    } else {
      setSelectedIds(new Set());
    }
  };

  const [paperAdded, setPaperAdded] = useState(false);

  const validQuestions = questions.filter((q) => q.isValid);
  const isMultiPage = totalPages > 1 && totalCount > validQuestions.length;
  const selectAll = isMultiPage
    ? (totalCount > 0 && selectedIds.size >= totalCount)
    : (validQuestions.length > 0 && validQuestions.every((q) => selectedIds.has(q.id)));

  const selectedCount = selectedIds.size;
  const isLimitMatched = questionLimit !== null && questionLimit > 0 ? selectedCount === questionLimit : selectedCount > 0;
  const canAddToPaper = !paperAdded && isLimitMatched;

  const handleAddToPaper = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canAddToPaper) return;
    const selectedQuestionIds = Array.from(selectedIds);
    if (selectedQuestionIds.length === 0) {
      alert("Please select at least one question to add.");
      return;
    }
    const isAiGenerated = chooseParams?.optionType === "ai_generated";
    const addFn = isAiGenerated ? addCategorizedQuestionsToPaper : addQuestionsToPaper;
    const res = await addFn({ testName, questionIds: selectedQuestionIds });
    if (res) {
      setPaperAdded(true);
    }
  };

  const handleComplete = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!paperAdded) return;
    setShowCompleteConfirm(true);
  };

  const handleConfirmComplete = async () => {
    setIsCompleting(true);
    try {
      const res = await completeQuestionPaper(testName);
      if (
        res &&
        (res.status === "completed" ||
          res.status === "success" ||
          res.paper_id ||
          res.test_id ||
          res.no_of_questions !== undefined)
      ) {
        setShowCompleteConfirm(false);
        if (onComplete) onComplete();
        if (onClose) onClose();
      }
    } finally {
      setIsCompleting(false);
    }
  };

  const currentSearchQuery = (debouncedSearchTerm || searchTerm || "").toLowerCase().trim();

  const filteredQuestions = questions.filter((q) => {
    const matchesSearch = !currentSearchQuery
      ? true
      : (
        (q.text && q.text.toLowerCase().includes(currentSearchQuery)) ||
        (q.optionA && q.optionA.toLowerCase().includes(currentSearchQuery)) ||
        (q.optionB && q.optionB.toLowerCase().includes(currentSearchQuery)) ||
        (q.optionC && q.optionC.toLowerCase().includes(currentSearchQuery)) ||
        (q.optionD && q.optionD.toLowerCase().includes(currentSearchQuery)) ||
        (q.optionE && q.optionE.toLowerCase().includes(currentSearchQuery)) ||
        (q.solution && q.solution.toLowerCase().includes(currentSearchQuery)) ||
        (q.level && q.level.toLowerCase().includes(currentSearchQuery)) ||
        (q.id && String(q.id).toLowerCase().includes(currentSearchQuery))
      );
    const qTypeRaw = (q.type || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const qRawTypeRaw = (q.rawType || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const filterTypeRaw = (filterType || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const matchesType =
      filterType === "All Question Types" ||
      filterType.toLowerCase() === "all" ||
      qTypeRaw === filterTypeRaw ||
      qRawTypeRaw === filterTypeRaw ||
      qTypeRaw.includes(filterTypeRaw) ||
      qRawTypeRaw.includes(filterTypeRaw) ||
      filterTypeRaw.includes(qTypeRaw) ||
      filterTypeRaw.includes(qRawTypeRaw) ||
      (filterTypeRaw.includes("numeric") && (qTypeRaw.includes("short") || qTypeRaw.includes("numeric") || qTypeRaw.includes("integer"))) ||
      (filterTypeRaw.includes("assertion") && qTypeRaw.includes("assertion")) ||
      (filterTypeRaw.includes("statement") && qTypeRaw.includes("statement"));

    const matchesLevel =
      filterLevel === "All Levels" ||
      !filterLevel ||
      (q.level && q.level.toLowerCase() === filterLevel.toLowerCase());

    const isUsed = q.is_used === true || (q.used_count !== undefined && Number(q.used_count) > 0);
    const matchesUsage = isImportFlow || !onlyNotUsed || !isUsed;

    const matchesValidation =
      filterValidation === "all" ||
      (filterValidation === "valid" && q.isValid) ||
      (filterValidation === "invalid" && !q.isValid);

    return matchesSearch && matchesType && matchesLevel && matchesUsage && matchesValidation;
  });

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (page > 3) {
        pages.push("...");
      }
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (page < totalPages - 2) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return (
      <div className="pagination">
        <button
          type="button"
          className="page_nav"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {`<`}
        </button>

        {pages.map((p, idx) => {
          if (p === "...") {
            return (
              <span key={`dots-${idx}`} className="dots">
                ...
              </span>
            );
          }
          return (
            <button
              key={p}
              type="button"
              className={`page_num ${page === p ? "active" : ""}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          );
        })}

        <button
          type="button"
          className="page_nav"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          {`>`}
        </button>
      </div>
    );
  };

  return (
    <div className="imported_modal_overlay" onClick={onClose}>
      <div className="imported_modal_box" onClick={(e) => e.stopPropagation()}>
        {/* Unique Header Container */}
        <div className="iqm_header_container">
          <div className="iqm_header_text">
            <h2 className="iqm_title">
              {isImportFlow ? "Imported Questions" : "Choose Questions"}
            </h2>
            <p className="iqm_subtitle">
              {isImportFlow
                ? "Review and manage questions parsed from your uploaded test document."
                : "Browse and select questions from the repository to build your test paper."}
            </p>
          </div>
          <svg
            className="iqm_close_icon"
            onClick={onClose}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4B5563"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>

        {/* Selected Test Card */}
        <div className="selected_test_card">
          <span className="card_label">Selected Test:</span>
          <h3 className="test_title">{testName}</h3>
        </div>

        {/* Filter Controls Bar */}
        <div className="filter_bar">
          <div className="search_box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search Questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <CustomTypeDropdown
            value={filterType}
            onChange={(val) => {
              setFilterType(val);
              setPage(1);
            }}
            options={
              dynamicTypeOptions.length > 0
                ? dynamicTypeOptions
                : (() => {
                  const uniqueQuestionTypes = Array.from(
                    new Set(questions.map((q) => q.rawType || q.type).filter(Boolean))
                  );
                  const baseTypes = ["MCQ", "Numeric", "Assertion and Reasoning", "Statement Question"];
                  const allAvailableTypes = Array.from(new Set([...baseTypes, ...uniqueQuestionTypes]));
                  return [
                    { value: "All Question Types", label: "All Question Types" },
                    ...allAvailableTypes.map((t) => ({ value: t, label: formatQuestionTypeLabel(t) })),
                  ];
                })()
            }
          />

          <CustomTypeDropdown
            value={filterLevel}
            onChange={(val) => {
              setFilterLevel(val);
              setPage(1);
            }}
            options={[
              { value: "All Levels", label: "All Levels" },
              { value: "Easy", label: "Easy" },
              { value: "Medium", label: "Medium" },
              { value: "Hard", label: "Hard" },
            ]}
          />

          <CustomTypeDropdown
            value={filterValidation}
            onChange={(val) => {
              setFilterValidation(val);
            }}
            options={[
              { value: "all", label: `All Questions (${questions.length})` },
              { value: "valid", label: `Valid (${questions.filter((q) => q.isValid).length})` },
              { value: "invalid", label: `Needs Review (${questions.filter((q) => !q.isValid).length})` },
            ]}
          />

          {(!isImportFlow || chooseParams) && (
            <CustomTypeDropdown
              value={filterConcept}
              onChange={(val) => {
                setFilterConcept(val);
                setPage(1);
              }}
              options={
                dynamicConceptOptions.length > 0
                  ? dynamicConceptOptions
                  : [{ value: "All Concepts", label: "All Concepts" }]
              }
            />
          )}

          {!isImportFlow && (
            <div
              className={`usage_toggle_container ${onlyNotUsed ? "active" : ""}`}
              onClick={() => setOnlyNotUsed((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOnlyNotUsed((prev) => !prev);
                }
              }}
              title={onlyNotUsed ? "Showing only not used questions (click to show all)" : "Click to show only not used questions"}
            >
              <span className="toggle_label_text">Not Used</span>
              <div className={`switch_track ${onlyNotUsed ? "active" : ""}`}>
                <div className={`switch_thumb ${onlyNotUsed ? "active" : ""}`} />
              </div>
            </div>
          )}

          <label className="select_all_label">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAllChange}
            />
            <span>Select All ({totalCount || questions.length})</span>
          </label>
        </div>

        {/* Question Cards List */}
        <div className="questions_list">
          {(isImportFlow && ["PARSING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(String(jobStatus).toUpperCase())) ? (
            <div className="iqm_parsing_container">
              <div className="iqm_round_spinner"></div>
              <h4>Parsing questions in progress...</h4>
              <p>
                Background parsing is running. Questions will automatically load once ready...
              </p>
            </div>
          ) : loading ? (
            <div className="iqm_parsing_container">
              <div className="iqm_round_spinner"></div>
              <p style={{ fontWeight: 600, color: "#374151" }}>Loading questions...</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>
              No questions found for this test paper.
            </div>
          ) : (
            filteredQuestions.map((q) => {
              const isMcq =
                (q.type || "").toLowerCase() === "mcq" ||
                Boolean(
                  q.optionA || q.optionB || q.optionC || q.optionD || q.optionE ||
                  (q.optionImages && (q.optionImages.A || q.optionImages.B || q.optionImages.C || q.optionImages.D || q.optionImages.E))
                );
              const normCorrect = getNormalizedCorrectAnswer(q.correct_answer);

              return (
                <div key={q.id} className={`question_card ${!q.isValid ? "has_error" : ""}`}>
                  <div className="card_top">
                    <div className="card_top_left">
                      {!q.isValid ? (
                        <div className="error_banner_row">
                          <span className="error_tag">Error</span>
                          <span className="error_msg">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                              <line x1="12" y1="9" x2="12" y2="13" />
                              <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                            {q.validationError || "This question could not be extracted correctly and need manual review"}
                          </span>
                        </div>
                      ) : (
                        <span className={`type_tag ${q.type ? q.type.toLowerCase().replace(/[^a-z0-9]+/g, "_") : "mcq"}`}>
                          {q.type}
                        </span>
                      )}

                      {q.is_used === false || (q.is_used === undefined && q.used_count !== undefined && Number(q.used_count) === 0) ? (
                        <span className="usage_badge not_used">
                          Not used
                        </span>
                      ) : Boolean((q.is_used || Number(q.used_count) > 0) && q.used_count !== undefined && Number(q.used_count) > 0) ? (
                        <span className="used_count_badge">
                          Used {q.used_count} {Number(q.used_count) === 1 ? "time" : "times"}
                        </span>
                      ) : q.is_used === true ? (
                        <span className="usage_badge used">
                          Used
                        </span>
                      ) : null}

                      {q.level && (
                        <span className={`level_badge ${String(q.level).toLowerCase().trim()}`}>
                          <span className="level_dot" />
                          {q.level}
                        </span>
                      )}
                    </div>

                    <button
                      className="edit_btn"
                      onClick={() => {
                        setEditingQuestion(q);
                        setEditPromptText(cleanLatexString(q.text || ""));
                        setEditOptionA(cleanLatexString(q.optionA || ""));
                        setEditOptionB(cleanLatexString(q.optionB || ""));
                        setEditOptionC(cleanLatexString(q.optionC || ""));
                        setEditOptionD(cleanLatexString(q.optionD || ""));
                        setEditOptionE(cleanLatexString(q.optionE || ""));
                        setEditCorrectAnswer(isMcq ? normCorrect : (q.correct_answer || ""));
                        setEditSolution(cleanLatexString(q.solution || ""));
                        setEditStemImage((q.imageUrls && q.imageUrls[0]) || null);
                        setEditOptionImages(
                          q.optionImages || { A: null, B: null, C: null, D: null, E: null }
                        );
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                  </div>

                  <div className="question_main">
                    <div className="q_left">
                      {q.isValid && (
                        <input
                          type="checkbox"
                          className="q_checkbox"
                          checked={selectedIds.has(q.id)}
                          onChange={() => toggleSelect(q.id)}
                        />
                      )}
                      <span className="q_num">{renderHighlightedString(q.id, debouncedSearchTerm || searchTerm)}</span>
                      <div className="q_text_block">
                        <p className="q_text"><LatexRenderer text={q.text} highlight={debouncedSearchTerm || searchTerm} /></p>
                      </div>
                    </div>
                  </div>

                  {q.imageUrls && q.imageUrls.length > 0 && (
                    <div className="q_image_container">
                      {q.imageUrls.map((imgUrl, i) => (
                        <div key={i} className="img_preview_box">
                          <img
                            src={imgUrl}
                            alt={`Question ${q.id} Diagram ${i + 1}`}
                            className="q_diagram_img"
                            onError={handleImageError}
                            onClick={(e) => handleOpenZoom(e, imgUrl, `Question ${q.id} Diagram ${i + 1}`)}
                            title="Click to zoom in"
                          />
                          <button
                            type="button"
                            className="img_zoom_action_btn"
                            title="Click to Zoom In"
                            onClick={(e) => handleOpenZoom(e, imgUrl, `Question ${q.id} Diagram ${i + 1}`)}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <circle cx="11" cy="11" r="8"></circle>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                              <line x1="11" y1="8" x2="11" y2="14"></line>
                              <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                            <span>Zoom In</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isMcq ? (
                    <div className="options_grid">
                      {[
                        { key: "A", text: q.optionA, img: q.optionImages?.A },
                        { key: "B", text: q.optionB, img: q.optionImages?.B },
                        { key: "C", text: q.optionC, img: q.optionImages?.C },
                        { key: "D", text: q.optionD, img: q.optionImages?.D },
                        ...(q.optionE || q.optionImages?.E ? [{ key: "E", text: q.optionE, img: q.optionImages?.E }] : []),
                      ].map((opt) => {
                        const isCorrect = normCorrect === opt.key;
                        return (
                          <div
                            key={opt.key}
                            className={`option_item ${isCorrect ? "correct_option" : ""}`}
                          >
                            <span className="opt_badge">{opt.key}</span>
                            <div className="opt_content">
                              {cleanOptionText(opt.text) !== "" && (
                                <span className="opt_text"><LatexRenderer text={cleanOptionText(opt.text)} inline highlight={debouncedSearchTerm || searchTerm} /></span>
                              )}
                              {opt.img && (
                                <div className="opt_img_wrapper">
                                  <div className="img_preview_box opt_box">
                                    <img
                                      src={opt.img}
                                      alt={`Option ${opt.key}`}
                                      className="opt_diagram_img"
                                      onError={handleImageError}
                                      onClick={(e) => handleOpenZoom(e, opt.img, `Question ${q.id} - Option ${opt.key}`)}
                                      title="Click to zoom in"
                                    />
                                    <button
                                      type="button"
                                      className="img_zoom_action_btn opt_zoom_btn"
                                      title="Click to Zoom In"
                                      onClick={(e) => handleOpenZoom(e, opt.img, `Question ${q.id} - Option ${opt.key}`)}
                                    >
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                      >
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        <line x1="11" y1="8" x2="11" y2="14"></line>
                                        <line x1="8" y1="11" x2="14" y2="11"></line>
                                      </svg>
                                      <span>Zoom In</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {isCorrect && (
                              <span className="correct_tag">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Correct
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="short_answer_block">
                      <div className="short_ans_header">
                        <span className="short_ans_label">Correct Answer:</span>
                      </div>
                      {q.correct_answer && q.correct_answer.trim() && q.correct_answer !== "Not specified." ? (
                        <div className="short_ans_box">
                          <LatexRenderer text={q.correct_answer} highlight={debouncedSearchTerm || searchTerm} />
                        </div>
                      ) : (
                        <div className="unspecified_ans_pill">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#991B1B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          <span>Please enter the answer</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Question & Options Edit Modal */}
        {editingQuestion && (
          <div
            className="eq_modal_overlay"
            onClick={() => setEditingQuestion(null)}
          >
            <div
              className="eq_modal_box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="eq_modal_header">
                <div>
                  <h3 className="eq_modal_title">Edit Question</h3>
                  <p className="eq_modal_subtitle">Review and correct the extracted text</p>
                </div>
                <svg
                  className="eq_modal_close"
                  onClick={() => setEditingQuestion(null)}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>

              {/* Question Text */}
              <div className="eq_section">
                <label className="eq_section_label">Question Text</label>
                <textarea
                  value={editPromptText}
                  onChange={(e) => setEditPromptText(e.target.value)}
                  rows={3}
                  className="eq_textarea"
                  placeholder="Enter question text..."
                />

                {/* Live Rendered LaTeX Preview */}
                {editPromptText && containsLatexPattern(editPromptText) && (
                  <div className="eq_live_preview_card">
                    <div className="eq_preview_header">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <span>Live Formula Preview</span>
                    </div>
                    <div className="eq_preview_body">
                      <LatexRenderer text={editPromptText} />
                    </div>
                  </div>
                )}

                {/* Stem Image Preview */}
                {editStemImage && (
                  <div className="stem_image_preview_box">
                    <img src={editStemImage} alt="Question Diagram" className="stem_preview_img" />
                    <button
                      type="button"
                      className="image_delete_badge"
                      onClick={(e) => handleStemImageDelete(e)}
                      title="Remove image"
                      disabled={uploadingTarget === "stem"}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Upload Question Image Button */}
                <div className="upload_btn_row">
                  <input
                    type="file"
                    accept="image/*"
                    id="stem-image-upload"
                    style={{ display: "none" }}
                    onChange={handleStemImageUpload}
                  />
                  <label htmlFor="stem-image-upload" className="eq_upload_btn">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span>{uploadingTarget === "stem" ? "Uploading..." : "Upload Image"}</span>
                  </label>
                </div>
              </div>

              {/* Answer Options or Numerical Answer */}
              {(() => {
                const isMcqType = (editingQuestion.type || "").toLowerCase() === "mcq";
                const hasTextOptions = Boolean(editOptionA || editOptionB || editOptionC || editOptionD || editOptionE);
                const hasImgOptions = Boolean(editOptionImages && (editOptionImages.A || editOptionImages.B || editOptionImages.C || editOptionImages.D || editOptionImages.E));
                const showMcqOptions = isMcqType || hasTextOptions || hasImgOptions;

                if (!showMcqOptions) {
                  return (
                    <div className="eq_section" style={{ marginTop: "16px" }}>
                      <label className="eq_section_label">Correct Answer</label>
                      <input
                        type="text"
                        value={editCorrectAnswer}
                        onChange={(e) => setEditCorrectAnswer(e.target.value)}
                        className="option_text_input"
                        style={{ width: "100%", height: "40px", padding: "0 14px", borderRadius: "8px", border: "1px solid #D1D5DB", outline: "none", fontWeight: 600, color: "#111827", background: "#fff" }}
                        placeholder="Enter correct answer (e.g. 5)"
                      />
                    </div>
                  );
                }

                return (
                  <div className="eq_section" style={{ marginTop: "20px" }}>
                    <label className="eq_section_label">Answer Options</label>
                    <div className="options_edit_list">
                      {[
                        { key: "A", val: editOptionA, setter: setEditOptionA },
                        { key: "B", val: editOptionB, setter: setEditOptionB },
                        { key: "C", val: editOptionC, setter: setEditOptionC },
                        { key: "D", val: editOptionD, setter: setEditOptionD },
                        ...(editingQuestion?.optionE || editingQuestion?.optionImages?.E || editOptionE || editOptionImages?.E
                          ? [{ key: "E", val: editOptionE, setter: setEditOptionE }]
                          : []),
                      ].map(({ key, val, setter }) => {
                        const optImgUrl = editOptionImages?.[key];
                        const inputId = `opt-${key.toLowerCase()}-image-upload`;
                        return (
                          <div key={key} className="option_edit_wrapper">
                            <div className="option_edit_row">
                              <div className="option_letter_badge">{key}</div>
                              <input
                                type="text"
                                value={val}
                                onChange={(e) => setter(e.target.value)}
                                className="option_text_input"
                                placeholder={`Option ${key}`}
                              />
                              {optImgUrl && (
                                <div className="option_image_thumb_box">
                                  <img src={optImgUrl} alt={`Option ${key}`} className="option_thumb_img" />
                                  <button
                                    type="button"
                                    className="image_delete_badge"
                                    onClick={(e) => handleOptionImageDelete(key, e)}
                                    title="Remove image"
                                    disabled={uploadingTarget === key}
                                  >
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <line x1="18" y1="6" x2="6" y2="18" />
                                      <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                id={inputId}
                                style={{ display: "none" }}
                                onChange={(e) => handleOptionImageUpload(key, e)}
                              />
                              <label htmlFor={inputId} className="option_upload_btn">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <span>{uploadingTarget === key ? "Uploading..." : "Image"}</span>
                              </label>
                            </div>
                            {val && containsLatexPattern(val) && (
                              <div className="option_live_preview">
                                <span className="option_preview_tag">Preview:</span>
                                <div className="option_preview_math">
                                  <LatexRenderer text={val} inline={true} />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="eq_section" style={{ marginTop: "16px" }}>
                      <label className="eq_section_label">Correct Answer Choice</label>
                      <select
                        value={editCorrectAnswer}
                        onChange={(e) => setEditCorrectAnswer(e.target.value)}
                        className="eq_select"
                        style={{
                          width: "100%",
                          height: "40px",
                          padding: "0 14px",
                          borderRadius: "8px",
                          border: "1px solid #D1D5DB",
                          fontWeight: 600,
                          color: "#111827",
                          outline: "none",
                          background: "#fff",
                          cursor: "pointer"
                        }}
                      >
                        <option value="A">Option A</option>
                        <option value="B">Option B</option>
                        <option value="C">Option C</option>
                        <option value="D">Option D</option>
                        {(editingQuestion?.optionE || editingQuestion?.optionImages?.E || editOptionE || editOptionImages?.E || editCorrectAnswer === "E") && (
                          <option value="E">Option E</option>
                        )}
                      </select>
                    </div>
                  </div>
                );
              })()}

              {/* Modal Footer Actions */}
              <div className="eq_modal_footer">
                <button
                  type="button"
                  className="eq_cancel_btn"
                  onClick={() => setEditingQuestion(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="eq_save_btn"
                  onClick={handleEditSave}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="modal_footer">
          <button
            type="button"
            className={`add_qp_btn ${paperAdded ? "added" : canAddToPaper ? "active" : ""}`}
            onClick={handleAddToPaper}
            disabled={!canAddToPaper}
          >
            <span>{paperAdded ? "Added to Paper ✓" : "Add to Question Paper"}</span>
            <span className="qp_count_badge">
              {questionLimit && questionLimit > 0
                ? `${selectedCount}/${questionLimit}`
                : selectedCount}
            </span>
          </button>

          {renderPagination()}

          <button
            type="button"
            className={`complete_qp_btn ${paperAdded ? "active" : ""}`}
            disabled={!paperAdded}
            onClick={handleComplete}
          >
            Complete Question Paper
          </button>
        </div>
        {/* Complete Question Paper Confirmation Popup */}
        {showCompleteConfirm && (
          <div
            className="complete_confirm_overlay"
            onClick={() => !isCompleting && setShowCompleteConfirm(false)}
          >
            <div
              className="complete_confirm_box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="confirm_icon_wrapper">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3 className="confirm_title">Finalize Question Paper?</h3>
              <p className="confirm_warning_text">
                Completing this action will finalize the question paper. Once completed, you will no longer be able to add or append questions from other sources.
              </p>
              <div className="confirm_test_badge">
                <span className="confirm_test_label">Test Paper:</span>
                <span className="confirm_test_name">{testName}</span>
              </div>
              <div className="confirm_actions">
                <button
                  type="button"
                  className="confirm_cancel_btn"
                  onClick={() => setShowCompleteConfirm(false)}
                  disabled={isCompleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm_proceed_btn"
                  onClick={handleConfirmComplete}
                  disabled={isCompleting}
                >
                  {isCompleting ? "Finalizing..." : "Confirm & Finalize"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Image Zoom Lightbox Modal ── */}
        {zoomModalImage && (
          <div
            className="qb_image_zoom_overlay"
            onClick={() => {
              setZoomModalImage(null);
              setZoomScale(1.5);
            }}
          >
            <div
              className="qb_image_zoom_modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="zoom_modal_header">
                <span className="zoom_modal_title">
                  {zoomModalImage.title || "Image Preview"}
                </span>
                <div className="zoom_controls_group">
                  <button
                    type="button"
                    className="zoom_ctrl_btn"
                    title="Zoom Out"
                    onClick={() => setZoomScale((prev) => Math.max(0.5, Number((prev - 0.25).toFixed(2))))}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <span className="zoom_scale_indicator">
                    {Math.round(zoomScale * 100)}%
                  </span>
                  <button
                    type="button"
                    className="zoom_ctrl_btn"
                    title="Zoom In"
                    onClick={() => setZoomScale((prev) => Math.min(3.5, Number((prev + 0.25).toFixed(2))))}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="11" y1="8" x2="11" y2="14"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="zoom_ctrl_btn reset_btn"
                    title="Reset Zoom"
                    onClick={() => setZoomScale(1.5)}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="zoom_close_btn"
                    title="Close (Esc)"
                    onClick={() => {
                      setZoomModalImage(null);
                      setZoomScale(1.5);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="zoom_modal_body">
                <div className="zoom_img_scroll_container">
                  <img
                    key={zoomModalImage.src}
                    src={zoomModalImage.src}
                    alt={zoomModalImage.title || "Zoomed diagram"}
                    className="zoom_preview_img"
                    style={{
                      transform: `scale(${zoomScale})`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
